import type { WeekStart } from '@awwyeah/shared';
import { useCallback, useSyncExternalStore } from 'react';

/**
 * Month/week views' first day of the week. Sunday is the default (owner call,
 * 2026-08-01); persisted per device like the theme and units preferences.
 */
export type WeekStartPreference = 'sunday' | 'monday';

export const WEEK_START_STORAGE_KEY = 'awwyeah:calendar:weekstart';

const listeners = new Set<() => void>();

function read(): WeekStartPreference {
  try {
    const raw = localStorage.getItem(WEEK_START_STORAGE_KEY);
    return raw === 'monday' ? 'monday' : 'sunday';
  } catch {
    return 'sunday';
  }
}

export function toWeekStart(pref: WeekStartPreference): WeekStart {
  return pref === 'monday' ? 1 : 0;
}

export function useWeekStart(): {
  preference: WeekStartPreference;
  weekStartsOn: WeekStart;
  setPreference: (pref: WeekStartPreference) => void;
} {
  const preference = useSyncExternalStore(
    (cb) => {
      listeners.add(cb);
      const onStorage = (e: StorageEvent): void => {
        if (e.key === WEEK_START_STORAGE_KEY) cb();
      };
      window.addEventListener('storage', onStorage);
      return () => {
        listeners.delete(cb);
        window.removeEventListener('storage', onStorage);
      };
    },
    read,
    () => 'sunday' as const,
  );

  const setPreference = useCallback((pref: WeekStartPreference) => {
    try {
      localStorage.setItem(WEEK_START_STORAGE_KEY, pref);
    } catch {
      /* private mode — the in-memory change below still applies this session */
    }
    for (const l of listeners) l();
  }, []);

  return { preference, weekStartsOn: toWeekStart(preference), setPreference };
}

/** Weekday header initials starting from the given week start. */
export function weekdayInitials(weekStartsOn: WeekStart): string[] {
  const sundayFirst = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
  return weekStartsOn === 0 ? sundayFirst : [...sundayFirst.slice(1), 'S'];
}
